import { useFormik } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { regulatoryService } from 'services';
import { RegulatoryInformation, RegulatoryInformationForm } from 'types';
import { Button, Heading, Input, Select, Text } from 'components';
import { formValidations, messages } from 'helpers';
import { BRAZIL_STATES, FontSize, FontWeight, routes } from 'commons';
import * as S from './styles';
import { CompletedModal } from '../completed-modal';
import { CurrencyInput } from '../currency-input';

const CNPJ_LENGTH = 14;
const CPF_LENGTH = 11;
const PHONE_LENGTH = 10;
const DATE_LENGTH = 10;
const MAX_INT = 2147483647;

function showLengthMessage({ field, value }: { field: string; value: number }) {
  messages([
    { errorCode: 'FRONT-RE-CHARACTERS_LENGTH_FIELD' },
    { subject: field, subjectType: 'field' },
    { subject: String(value), subjectType: 'length' },
  ]);
}

function getPhoneAndDDD(phoneNumber: string) {
  return {
    phoneNumberDDD: phoneNumber.slice(0, 2),
    phoneNumber: phoneNumber.slice(2),
  };
}

function formatDate(date: string) {
  return `${date.slice(3, 5)}/${date.slice(0, 2)}/${date.slice(6)}`;
}

interface Props {
  store: string;
  initialValues: RegulatoryInformationForm;
}

export function RegulatoryForm({ store, initialValues }: Props) {
  const navigate = useNavigate();

  const [isSendingForm, setIsSendingForm] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  function finishForm() {
    navigate(routes.profile.path);
  }

  const formValidation = Yup.object().shape({
    cpfCnpj: Yup.string().required('CNPJ'),
    tradingName: Yup.string().required('Nome Fantasia'),
    name: Yup.string().required('Razão Social'),
    annualRevenue: Yup.string().required('Faturamento Anual'),
    email: Yup.string()
      .required('E-mail da loja')
      .email('E-mail da loja, com formato correto é'),
    phone: Yup.string().required('Telefone da loja'),
    document: Yup.string().required('CNPJ da conta'),
    bankCode: Yup.string().required('Banco'),
    agencyCode: Yup.string().required('Agência'),
    accountNumber: Yup.string().required('Conta'),
    accountDigit: Yup.string().required('Dígito da Conta'),
    address: Yup.object().shape({
      street: Yup.string().required('Logradouro da loja'),
      streetNumber: Yup.string().required('Número da loja'),
      city: Yup.string().required('Cidade da loja'),
      cep: Yup.string().required('CEP da loja'),
      addressReferencePoint: Yup.string().required(
        'Ponto de referência da loja'
      ),
      neighborhood: Yup.string().required('Bairro da loja'),
      state: Yup.string().required('Estado da loja'),
    }),
    managingPartner: Yup.object().shape({
      name: Yup.string().required('Nome do Representante Legal'),
      email: Yup.string()
        .required('Email do Representante Legal')
        .email('Email do Representante Legal, com formato correto é'),
      cpfCnpj: Yup.string().required('CPF do Representante Legal'),
      birthday: Yup.string().required(
        'Data de Nascimento do Representante Legal'
      ),
      monthlyIncome: Yup.string().required(
        'Faturamento Mensal do Representante Legal'
      ),
      professionalOccupation: Yup.string().required(
        'Ocupação Profissional do Representante Legal'
      ),
      selfDeclaredLegalPresentative: Yup.boolean().required(
        'Representante Legal é auto declarado'
      ),
      phone: Yup.string().required('Telefone do Representante Legal'),
      address: Yup.object().shape({
        street: Yup.string().required('Logradouro do Representante Legal'),
        streetNumber: Yup.string().required('Número do Representante Legal'),
        city: Yup.string().required('Cidade do Representante Legal'),
        cep: Yup.string().required('CEP do Representante Legal'),
        addressReferencePoint: Yup.string().required(
          'Ponto de referência do Representante Legal'
        ),
        neighborhood: Yup.string().required('Bairro do Representante Legal'),
        state: Yup.string().required('Estado do Representante Legal'),
      }),
    }),
  });

  const { values, errors, handleChange, handleSubmit, setFieldValue } =
    useFormik<RegulatoryInformationForm>({
      initialValues: {
        ...initialValues,
      },

      validateOnBlur: false,

      validateOnChange: false,

      validationSchema: formValidation,

      validate: (values) => {
        formValidations.validationSchema({
          schema: formValidation,
          values,
        });
      },

      onSubmit: async (values, { setErrors }) => {
        setIsSendingForm(true);

        if (store === null) {
          setIsSendingForm(false);
          return toast.warn(`A loja precisa ser informada`);
        }

        const {
          cpfCnpj,
          phone,
          tradingName,
          name,
          annualRevenue,
          email,
          address,
          managingPartner,
          accountDigit,
          accountNumber,
          agencyCode,
          agencyDigit,
          bankCode,
          document,
        } = values;

        const formattedCpfCnpj = cpfCnpj.replace(/[^0-9]/g, '');
        if (formattedCpfCnpj.length < CNPJ_LENGTH) {
          showLengthMessage({ field: 'CNPJ', value: CNPJ_LENGTH });
          setIsSendingForm(false);
          return setErrors({ cpfCnpj: 'error' });
        }

        const formattedPartnerCpfCnpj = managingPartner.cpfCnpj.replace(
          /[^0-9]/g,
          ''
        );
        if (formattedPartnerCpfCnpj.length < CPF_LENGTH) {
          showLengthMessage({
            field: 'CPF do Representante Legal',
            value: CPF_LENGTH,
          });
          setIsSendingForm(false);
          return setErrors({ managingPartner: { cpfCnpj: 'error' } });
        }

        const formattedStorePhone = phone.replace(/[^0-9]/g, '');
        if (formattedStorePhone.length < PHONE_LENGTH) {
          showLengthMessage({ field: 'Telefone da Loja', value: PHONE_LENGTH });
          setIsSendingForm(false);
          return setErrors({ phone: 'error' });
        }

        const formattedPartnerPhone = managingPartner.phone.replace(
          /[^0-9]/g,
          ''
        );
        if (formattedPartnerPhone.length < PHONE_LENGTH) {
          showLengthMessage({
            field: 'Telefone do Representante Legal',
            value: PHONE_LENGTH,
          });
          setIsSendingForm(false);
          return setErrors({ managingPartner: { phone: 'error' } });
        }

        const formattedDate = new Date(formatDate(managingPartner.birthday));
        if (
          managingPartner.birthday.length < DATE_LENGTH ||
          isNaN(formattedDate.getDate())
        ) {
          toast.warn(`A data informada é inválida`);
          setIsSendingForm(false);
          return setErrors({ managingPartner: { birthday: 'error' } });
        }

        const numAnnualRevenue = Number(annualRevenue);
        if (numAnnualRevenue > MAX_INT) {
          toast.warn(`Valor de faturamento anual informado é muito alto`);
          setIsSendingForm(false);
          return setErrors({ annualRevenue: 'error' });
        }

        const numMonthlyIncome = Number(managingPartner.monthlyIncome);
        if (numMonthlyIncome > MAX_INT) {
          toast.warn(`Valor de faturamento mensal informado é muito alto`);
          setIsSendingForm(false);
          return setErrors({ managingPartner: { monthlyIncome: 'error' } });
        }

        const storePhone = getPhoneAndDDD(formattedStorePhone);
        const partnerPhone = getPhoneAndDDD(formattedPartnerPhone);

        const params: RegulatoryInformation = {
          vendorRegisterInformation: {
            cpfCnpj: formattedCpfCnpj,
            tradingName,
            name,
            annualRevenue: Number(annualRevenue),
            email,
            phone: storePhone,
            address: {
              street: address.street,
              streetNumber: address.streetNumber,
              city: address.city,
              cep: address.cep,
              addressReferencePoint: address.addressReferencePoint,
              neighborhood: address.neighborhood,
              state: address.state,
            },
            managingPartner: {
              name: managingPartner.name,
              email: managingPartner.email,
              cpfCnpj: formattedPartnerCpfCnpj,
              birthday: formattedDate.getTime(),
              monthlyIncome: Number(managingPartner.monthlyIncome),
              professionalOccupation: managingPartner.professionalOccupation,
              selfDeclaredLegalPresentative: Boolean(
                managingPartner.selfDeclaredLegalPresentative
              ),
              phone: partnerPhone,
              address: {
                street: managingPartner.address.street,
                streetNumber: managingPartner.address.streetNumber,
                city: managingPartner.address.city,
                cep: managingPartner.address.cep,
                addressReferencePoint:
                  managingPartner.address.addressReferencePoint,
                neighborhood: managingPartner.address.neighborhood,
                state: managingPartner.address.state,
              },
            },
          },
          accountDigit,
          accountNumber,
          agencyCode,
          agencyDigit: agencyDigit ? agencyDigit : undefined,
          bankCode,
          document,
        };

        const response = await regulatoryService.postForm({ store, params });

        if (response.status === 200) {
          setIsCompleted(true);
        }

        setIsSendingForm(false);
      },
    });

  const {
    cpfCnpj,
    tradingName,
    name,
    email,
    phone,
    address,
    managingPartner,
    document,
    bankCode,
    agencyCode,
    agencyDigit,
    accountNumber,
    accountDigit,
  } = values;

  return (
    <S.Main>
      {isCompleted && <CompletedModal finishForm={finishForm} />}

      <Heading as="h1" weight={FontWeight.bold}>
        Adequação para Mudanças Regulatórias
      </Heading>

      <div>
        <Text size={FontSize.font12}>
          Sócio/Representante Legal, preencha o formulário abaixo para
          regularizar o recebimento de valores junto ao adquirente, de acordo
          com a Normativa do BACEN circular 3.978/20.
        </Text>
        <Text className="danger" size={FontSize.font12}>
          * Campos obrigatórios
        </Text>
      </div>

      <S.Form onSubmit={handleSubmit}>
        <S.FormContainer>
          <S.FormSection>
            <S.Title>
              <Heading as="h3" size={FontSize.font16} weight={FontWeight.bold}>
                Informações da loja
              </Heading>
            </S.Title>

            <S.FormGrid>
              <S.InputWrapper>
                <S.Label htmlFor="cpfCnpj">
                  Número do CNPJ <span>*</span>
                </S.Label>
                <Input
                  status={errors.cpfCnpj ? 'error' : 'default'}
                  name="cpfCnpj"
                  value={cpfCnpj}
                  mask="99.999.999/9999-99"
                  onChange={handleChange}
                  placeholder="CNPJ da loja"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="tradingName">
                  Nome Fantasia <span>*</span>
                </S.Label>
                <Input
                  status={errors.tradingName ? 'error' : 'default'}
                  name="tradingName"
                  value={tradingName}
                  onChange={handleChange}
                  placeholder="Nome Fantasia do recebedor"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="name">
                  Razão Social <span>*</span>
                </S.Label>
                <Input
                  status={errors.name ? 'error' : 'default'}
                  name="name"
                  value={name}
                  onChange={handleChange}
                  placeholder="Razão Social"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="annualRevenue">
                  Faturamento Anual <span>*</span>
                </S.Label>

                <CurrencyInput
                  defaultValue={values.annualRevenue}
                  status={errors.annualRevenue ? 'error' : 'default'}
                  name="annualRevenue"
                  setFormValue={(newValue) => {
                    setFieldValue('annualRevenue', newValue);
                  }}
                  placeholder="Disponível em Janeiro de 2024"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="email">
                  E-mail <span>*</span>
                </S.Label>
                <Input
                  status={errors.email ? 'error' : 'default'}
                  name="email"
                  value={email}
                  onChange={handleChange}
                  placeholder="E-mail da loja"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="phone">
                  Número de telefone <span>*</span>
                </S.Label>
                <Input
                  status={errors.phone ? 'error' : 'default'}
                  name="phone"
                  value={phone}
                  mask="(99) 99999-9999"
                  onChange={handleChange}
                  placeholder="Número de telefone da loja"
                />
              </S.InputWrapper>
            </S.FormGrid>
          </S.FormSection>

          <S.FormSection>
            <S.Title>
              <Heading as="h3" size={FontSize.font16} weight={FontWeight.bold}>
                Informações Bancárias
              </Heading>
            </S.Title>

            <S.FormGrid>
              <S.InputWrapper>
                <S.Label htmlFor="document">
                  CNPJ da conta <span>*</span>
                </S.Label>
                <Input
                  status={errors.document ? 'error' : 'default'}
                  name="document"
                  value={document}
                  mask="99.999.999/9999-99"
                  onChange={handleChange}
                  placeholder="Número do CNPJ da conta bancária"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="bankCode">
                  Banco <span>*</span>
                </S.Label>
                <Input
                  status={errors.bankCode ? 'error' : 'default'}
                  name="bankCode"
                  value={bankCode}
                  onChange={handleChange}
                  placeholder="Código do Banco"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="agencyCode">
                  Agência <span>*</span>
                </S.Label>
                <Input
                  status={errors.agencyCode ? 'error' : 'default'}
                  name="agencyCode"
                  value={agencyCode}
                  onChange={handleChange}
                  placeholder="Número da agência bancária"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="agencyDigit">Dígito da Agência</S.Label>

                <Input
                  status={errors.agencyDigit ? 'error' : 'default'}
                  name="agencyDigit"
                  value={agencyDigit}
                  onChange={handleChange}
                  placeholder="Número do dígito da agência, caso houver"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="accountNumber">
                  Conta <span>*</span>
                </S.Label>
                <Input
                  status={errors.accountNumber ? 'error' : 'default'}
                  name="accountNumber"
                  value={accountNumber}
                  onChange={handleChange}
                  placeholder="Número da conta bancária"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="accountDigit">
                  Dígito da Conta <span>*</span>
                </S.Label>
                <Input
                  status={errors.accountDigit ? 'error' : 'default'}
                  name="accountDigit"
                  value={accountDigit}
                  onChange={handleChange}
                  placeholder="Número do dígito da conta, caso houver"
                />
              </S.InputWrapper>
            </S.FormGrid>
          </S.FormSection>

          <S.FormSection>
            <S.Title>
              <Heading as="h3" size={FontSize.font16} weight={FontWeight.bold}>
                Endereço completo da loja
              </Heading>
            </S.Title>

            <S.FormGrid>
              <S.InputWrapper>
                <S.Label htmlFor="address.street">
                  Logradouro <span>*</span>
                </S.Label>
                <Input
                  status={errors.address?.street ? 'error' : 'default'}
                  name="address.street"
                  value={address.street}
                  onChange={handleChange}
                  placeholder="Nome da rua"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="address.streetNumber">
                  Número <span>*</span>
                </S.Label>
                <Input
                  status={errors.address?.streetNumber ? 'error' : 'default'}
                  name="address.streetNumber"
                  value={address.streetNumber}
                  onChange={handleChange}
                  placeholder="Número do endereço da loja"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="address.cep">
                  CEP <span>*</span>
                </S.Label>
                <Input
                  status={errors.address?.cep ? 'error' : 'default'}
                  mask="99999-999"
                  name="address.cep"
                  value={address.cep}
                  onChange={handleChange}
                  placeholder="Digite o CEP da loja"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="address.city">
                  Cidade <span>*</span>
                </S.Label>
                <Input
                  status={errors.address?.city ? 'error' : 'default'}
                  name="address.city"
                  value={address.city}
                  onChange={handleChange}
                  placeholder="Digite a cidade da loja"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="address.addressReferencePoint">
                  Ponto de Referência <span>*</span>
                </S.Label>
                <Input
                  status={
                    errors.address?.addressReferencePoint ? 'error' : 'default'
                  }
                  name="address.addressReferencePoint"
                  value={address.addressReferencePoint}
                  onChange={handleChange}
                  placeholder="Ponto de Referência da loja"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="address.neighborhood">
                  Bairro <span>*</span>
                </S.Label>
                <Input
                  status={errors.address?.neighborhood ? 'error' : 'default'}
                  name="address.neighborhood"
                  value={address.neighborhood}
                  onChange={handleChange}
                  placeholder="Bairro da loja"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="address.state">
                  Estado <span>*</span>
                </S.Label>
                <Select.Wrapper
                  status={errors.address?.state ? 'error' : 'default'}
                  name="address.state"
                  options={BRAZIL_STATES}
                  onChange={handleChange}
                >
                  <Select.Input placeholder="Selecione o estado da loja" />

                  <Select.StatusArrow />
                </Select.Wrapper>
              </S.InputWrapper>
            </S.FormGrid>
          </S.FormSection>

          <S.FormSection>
            <S.Title>
              <Heading as="h3" size={FontSize.font16} weight={FontWeight.bold}>
                Informações do Sócio/Representante Legal listados no CNPJ
              </Heading>
            </S.Title>

            <S.FormGrid>
              <S.InputWrapper>
                <S.Label htmlFor="managingPartner.name">
                  Nome <span>*</span>
                </S.Label>
                <Input
                  status={errors.managingPartner?.name ? 'error' : 'default'}
                  name="managingPartner.name"
                  value={managingPartner.name}
                  onChange={handleChange}
                  placeholder="Nome do Representante Legal"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="managingPartner.email">
                  E-mail <span>*</span>
                </S.Label>
                <Input
                  status={errors.managingPartner?.email ? 'error' : 'default'}
                  name="managingPartner.email"
                  value={managingPartner.email}
                  onChange={handleChange}
                  placeholder="E-mail do Sócio/Representante Legal"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="managingPartner.cpfCnpj">
                  CPF <span>*</span>
                </S.Label>
                <Input
                  status={errors.managingPartner?.cpfCnpj ? 'error' : 'default'}
                  mask="999.999.999-99"
                  name="managingPartner.cpfCnpj"
                  value={managingPartner.cpfCnpj}
                  onChange={handleChange}
                  placeholder="CPF do Representante Legal"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="managingPartner.birthday">
                  Data de nascimento <span>*</span>
                </S.Label>
                <Input
                  status={
                    errors.managingPartner?.birthday ? 'error' : 'default'
                  }
                  mask="99/99/9999"
                  name="managingPartner.birthday"
                  value={managingPartner.birthday}
                  onChange={handleChange}
                  placeholder="dd/mm/aaaa"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="managingPartner.monthlyIncome">
                  Renda Mensal do sócio <span>*</span>
                </S.Label>
                <CurrencyInput
                  defaultValue={managingPartner.monthlyIncome}
                  status={
                    errors.managingPartner?.monthlyIncome ? 'error' : 'default'
                  }
                  name="managingPartner.monthlyIncome"
                  setFormValue={(newValue) => {
                    setFieldValue('managingPartner.monthlyIncome', newValue);
                  }}
                  placeholder="Disponível em Janeiro de 2024"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="managingPartner.professionalOccupation">
                  Ocupação Profissional <span>*</span>
                </S.Label>
                <Input
                  status={
                    errors.managingPartner?.professionalOccupation
                      ? 'error'
                      : 'default'
                  }
                  name="managingPartner.professionalOccupation"
                  value={managingPartner.professionalOccupation}
                  onChange={handleChange}
                  placeholder="Ex: Gerente, Direto"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label>
                  É Representante Legal Auto Declarado? <span>*</span>
                </S.Label>
                <S.FormRow>
                  <S.RadioWrapper>
                    <input
                      type="radio"
                      name="managingPartner.selfDeclaredLegalPresentative"
                      value={'yes'}
                      checked={
                        managingPartner.selfDeclaredLegalPresentative === true
                      }
                      onChange={() =>
                        setFieldValue(
                          'managingPartner.selfDeclaredLegalPresentative',
                          true
                        )
                      }
                    />
                    <Text
                      className={
                        errors.managingPartner?.selfDeclaredLegalPresentative
                          ? 'error'
                          : 'default'
                      }
                    >
                      Sim
                    </Text>
                  </S.RadioWrapper>

                  <S.RadioWrapper>
                    <input
                      type="radio"
                      name="managingPartner.selfDeclaredLegalPresentative"
                      value={'no'}
                      checked={
                        managingPartner.selfDeclaredLegalPresentative === false
                      }
                      onChange={() =>
                        setFieldValue(
                          'managingPartner.selfDeclaredLegalPresentative',
                          false
                        )
                      }
                    />
                    <Text
                      className={
                        errors.managingPartner?.selfDeclaredLegalPresentative
                          ? 'error'
                          : 'default'
                      }
                    >
                      Não
                    </Text>
                  </S.RadioWrapper>
                </S.FormRow>
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="managingPartner.phone">
                  Número de telefone <span>*</span>
                </S.Label>
                <Input
                  status={errors.managingPartner?.phone ? 'error' : 'default'}
                  name="managingPartner.phone"
                  value={managingPartner.phone}
                  mask="(99) 99999-9999"
                  onChange={handleChange}
                  placeholder="Digite o telefone do Representante Legal"
                />
              </S.InputWrapper>
            </S.FormGrid>
          </S.FormSection>

          <S.FormSection>
            <S.Title>
              <Heading as="h3" size={FontSize.font16} weight={FontWeight.bold}>
                Endereço completo do Sócio/Representante Legal
              </Heading>
            </S.Title>

            <S.FormGrid>
              <S.InputWrapper>
                <S.Label htmlFor="managingPartner.address.street">
                  Logradouro <span>*</span>
                </S.Label>
                <Input
                  status={
                    errors.managingPartner?.address?.street
                      ? 'error'
                      : 'default'
                  }
                  name="managingPartner.address.street"
                  value={managingPartner.address.street}
                  onChange={handleChange}
                  placeholder="Nome da rua do Representante Legal"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="managingPartner.address.streetNumber">
                  Número <span>*</span>
                </S.Label>
                <Input
                  status={
                    errors.managingPartner?.address?.streetNumber
                      ? 'error'
                      : 'default'
                  }
                  name="managingPartner.address.streetNumber"
                  value={managingPartner.address.streetNumber}
                  onChange={handleChange}
                  placeholder="Número da rua do Representante Legal"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="managingPartner.address.cep">
                  CEP <span>*</span>
                </S.Label>
                <Input
                  status={
                    errors.managingPartner?.address?.cep ? 'error' : 'default'
                  }
                  mask="99999-999"
                  name="managingPartner.address.cep"
                  value={managingPartner.address.cep}
                  onChange={handleChange}
                  placeholder="Digite o CEP do Representante Legal"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="managingPartner.address.city">
                  Cidade <span>*</span>
                </S.Label>
                <Input
                  status={
                    errors.managingPartner?.address?.city ? 'error' : 'default'
                  }
                  name="managingPartner.address.city"
                  value={managingPartner.address.city}
                  onChange={handleChange}
                  placeholder="Digite a cidade do Representante Legal"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="managingPartner.address.addressReferencePoint">
                  Ponto de Referência <span>*</span>
                </S.Label>
                <Input
                  status={
                    errors.managingPartner?.address?.addressReferencePoint
                      ? 'error'
                      : 'default'
                  }
                  name="managingPartner.address.addressReferencePoint"
                  value={managingPartner.address.addressReferencePoint}
                  onChange={handleChange}
                  placeholder="Ponto de referência do Representante Legal"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="managingPartner.address.neighborhood">
                  Bairro <span>*</span>
                </S.Label>
                <Input
                  status={
                    errors.managingPartner?.address?.neighborhood
                      ? 'error'
                      : 'default'
                  }
                  name="managingPartner.address.neighborhood"
                  value={managingPartner.address.neighborhood}
                  onChange={handleChange}
                  placeholder="Bairro do Representante Legal"
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label htmlFor="managingPartner.address.state">
                  Estado <span>*</span>
                </S.Label>
                <Select.Wrapper
                  status={
                    errors.managingPartner?.address?.state ? 'error' : 'default'
                  }
                  name="managingPartner.address.state"
                  options={BRAZIL_STATES}
                  onChange={handleChange}
                >
                  <Select.Input placeholder="Selecione o estado do Representante Legal" />

                  <Select.StatusArrow />
                </Select.Wrapper>
              </S.InputWrapper>
            </S.FormGrid>
          </S.FormSection>
        </S.FormContainer>

        <S.ButtonWrapper>
          <Button
            type="submit"
            isLoading={isSendingForm}
            disabled={isSendingForm}
          >
            Enviar
          </Button>
        </S.ButtonWrapper>
      </S.Form>
    </S.Main>
  );
}
